/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import produce from 'immer';
import isUndefined from 'lodash.isundefined';
import {error} from '@ion/core/src/logger/logger';
import {
  acknowledgeAlerts,
  getAlertDetails
} from '@ion/core/src/services/Alerts';
import {toNotificationsAccount} from '@ion/core/src/services/FetchWorkerUtils';
import {
  AlertFired,
  AlertSeverity,
  GenericObject
} from '@ion/core/src/types/entities';
import {idMap} from '@ion/core/src/utils/ObjectUtil';
import NotificationsWorker from '@ion/core/src/workers/NotificationsWorker';
import {
  Notification,
  NotificationAction,
  NotificationCategory
} from '@ion/dls/src/types/extensions';
import {NotificationPanelTabs} from '@ion/dls/src/types/types';
import {AsyncState} from '../types/redux';

// name of the reducer
export const NAME = 'notificationsPanel';

/**
 * ACTIONS
 */
export const SET_NOTIFICATIONS_PANEL_OPEN       = 'SET_NOTIFICATIONS_PANEL_OPEN';
export const SET_NOTIFICATIONS_ACTIVE_PANEL     = 'SET_NOTIFICATIONS_ACTIVE_PANEL';
export const SET_NOTIFICATION_PANEL_STATE       = 'SET_NOTIFICATION_PANEL_STATE';
export const REMOVE_TOAST_NOTIFICATION          = 'REMOVE_TOAST_NOTIFICATION';

export const GET_NOTIFICATION_DETAILS_REQUEST   = 'GET_NOTIFICATION_DETAILS_REQUEST';
export const GET_NOTIFICATION_DETAILS_RESPONSE  = 'GET_NOTIFICATION_DETAILS_RESPONSE';
export const GET_NOTIFICATION_DETAILS_ERROR     = 'GET_NOTIFICATION_DETAILS_ERROR';

export const MARK_AS_READ_REQUEST               = 'MARK_AS_READ_REQUEST';
export const MARK_AS_READ_RESPONSE              = 'MARK_AS_READ_RESPONSE';
export const MARK_AS_READ_ERROR                 = 'MARK_AS_READ_ERROR';

export const MARK_AS_CLEARED_REQUEST            = 'MARK_AS_CLEARED_REQUEST';
export const MARK_AS_CLEARED_RESPONSE           = 'MARK_AS_CLEARED_RESPONSE';
export const MARK_AS_CLEARED_ERROR              = 'MARK_AS_CLEARED_ERROR';

export const POST_INITIALIZATION_REQUEST        = 'POST_INITIALIZATION_REQUEST';
export const POST_INITIALIZATION_COMPLETE       = 'POST_INITIALIZATION_COMPLETE';
export const POST_INITIALIZATION_ERROR          = 'POST_INITIALIZATION_ERROR';

export const ALERTS_REFRESH_ACTIVATED           = 'ALERTS_REFRESH_ACTIVATED';
export const ALERTS_REFRESH_UPDATED             = 'ALERTS_REFRESH_UPDATED';
export const ALERTS_REFRESH_DEACTIVATED         = 'ALERTS_REFRESH_DEACTIVATED';

export const SELECT_NOTIFICATION                = 'SELECT_NOTIFICATION';
export const SELECT_ALL_NOTIFICATIONS           = 'SELECT_ALL_NOTIFICATIONS';
export const DESELECT_ALL_NOTIFICATIONS         = 'DESELECT_ALL_NOTIFICATIONS';

// reset notification state
export const RESET_NOTIFICATION_REQUEST         = 'RESET_NOTIFICATION_REQUEST';
export const RESET_NOTIFICATION_RESPONSE        = 'RESET_NOTIFICATION_RESPONSE';
export const RESET_NOTIFICATION_ERROR           = 'RESET_NOTIFICATION_ERROR';

export const SHOW_NOTIFICATION_FILTERS          = 'SHOW_NOTIFICATION_FILTERS';

export const JOB_NOTIFICATION_CREATED           = 'JOB_NOTIFICATION_CREATED';

/**
 * The prefix every job notification's `uuid` carries. A job notification is not
 * an alert, so the routes into the alert API have to be able to recognise one
 * and stand down - see `isJobNotification`.
 */
export const JOB_NOTIFICATION_PREFIX = 'job-';

/**
 * Whether this notification describes a finished job rather than a fired alert.
 *
 * Alert ids and job ids are both plain integers from unrelated tables, so a
 * job id reaching `acknowledgeAlerts` or `getAlertDetails` would address an
 * unrelated *alert*. The `uuid` prefix is the only thing which distinguishes
 * the two, which is why it is a shared constant rather than a literal.
 */
export const isJobNotification = (notification?: Notification): boolean => (
  Boolean(notification?.uuid?.startsWith(JOB_NOTIFICATION_PREFIX))
);

/**
 * The `uuid` a job notification carries for a given job - the encode half of
 * the prefix, colocated with the constant and with its own decode so the three
 * cannot drift apart across the packages which use them.
 *
 * @param   jobId The job the notification describes.
 * @returns The prefixed uuid.
 */
export const jobNotificationUuid = (jobId?: number | string): string => (
  `${JOB_NOTIFICATION_PREFIX}${jobId}`
);

/**
 * The job id a job notification's `uuid` carries - the decode half.
 *
 * `isJobNotification` is the only guarantee the prefix is there, so read this
 * behind that check.
 *
 * @param   uuid The notification's uuid.
 * @returns The job id, or `NaN` when the uuid carried none.
 */
export const jobNotificationJobId = (uuid?: string): number => (
  Number(uuid?.replace(JOB_NOTIFICATION_PREFIX, ''))
);

let clearedHandler: ((notifications: Notification[]) => void) | null = null;

/**
 * Registers the handler invoked with every cleared notification, and returns a
 * function which removes it.
 *
 * `markAllAsCleared` is local-only - clearing a notification hits no service -
 * so an application which persists its notifications needs this hook to make a
 * clear survive a reload.
 *
 * ```typescript
 *
 *   import {onNotificationsCleared} from '@ion/applications/src/notifications/state';
 *
 *   const dispose = onNotificationsCleared(deleteStoredNotifications);
 *
 * ```
 *
 * @param   handler Invoked with the notifications which were just cleared.
 * @returns A function which removes the handler when invoked.
 */
export const onNotificationsCleared = (
  handler: (notifications: Notification[]) => void
): (() => void) => {
  clearedHandler = handler;

  return () => {
    clearedHandler = null;
  };
};

/**
  * @setNotificationsPanelStatus
  */
export const setNotificationsPanelStatus = (state: boolean) => ({
  type : SET_NOTIFICATIONS_PANEL_OPEN, open : state
});

/**
  * @function setNotificationPanelState
  */
export const setNotificationPanelState = (newState: PanelState[]) => {
  return {type: SET_NOTIFICATION_PANEL_STATE, newState};
};

/**
  * @setNotificationsActivePanel
  */
export const setNotificationsActivePanel = (activePanel: NotificationPanelTabs) => ({
  type : SET_NOTIFICATIONS_ACTIVE_PANEL, activePanel
});

/**
  * @removeToastNotification
  */
export const removeToastNotification = (index: number) => ({
  type : REMOVE_TOAST_NOTIFICATION, index
});

/**
  * @getNotificationDetails
  */
export const getNotificationDetails = (notification: Notification) => {
  return async dispatch => {
    dispatch({type: GET_NOTIFICATION_DETAILS_REQUEST});
    try {
      const details = await getAlertDetails(notification);
      dispatch({type: GET_NOTIFICATION_DETAILS_RESPONSE, notification: {...notification, details}});
    } catch (err) {
      error(err);
      dispatch({type: GET_NOTIFICATION_DETAILS_ERROR});
    }
  };
};

/**
  * Provides the action which facilitates marking an individual notification as read.
  */
export const markAsRead = (notification: Notification) => {
  return async dispatch => {
    dispatch(markAllAsRead([notification]));
  };
};

/**
  * Provides the action which facilitates marking a collection of notifications as read.
  * This action corresponds to the "Mark All as Read" and "Mark Selected as Read"
  * Notification Features.
  *
  */
export const markAllAsRead = (notifications: Notification[]) => {
  return async dispatch => {
    dispatch({type: MARK_AS_READ_REQUEST, notifications});
    try {
      await acknowledgeAlerts(notifications.map(({id}) => id) as number[]);
      dispatch({type: MARK_AS_READ_RESPONSE, notifications});
      dispatch(deselectAllNotifications());
    } catch (err) {
      error(err);
      dispatch({type: MARK_AS_READ_ERROR});
    }
  };
};

/**
  * Provides the action which facilitates marking an individual notification as cleared.
  */
export const markAsCleared = (notification: Notification) => {
  return async dispatch => {
    dispatch(markAllAsCleared([notification]));
  };
};

/**
  * Provides the action which facilitates marking a collection of notifications as cleared.
  * This action corresponds to the "Mark All as Cleared" and "Mark Selected as Cleared"
  * Notification Features.
  *
  */
export const markAllAsCleared = (notifications: Notification[]) => {
  return async dispatch => {
    dispatch({type: MARK_AS_CLEARED_REQUEST, notifications});
    try {
      dispatch({type: MARK_AS_CLEARED_RESPONSE, notifications});
      clearedHandler?.(notifications);
      dispatch(deselectAllNotifications());
    } catch (err) {
      error(err);
      dispatch({type: MARK_AS_CLEARED_ERROR});
    }
  };
};

/**
  *
  * Adds or removes a notification from an array that
  * tracks which notifications have been selected by the checkboxes
  * for performing bulk actions on the list of selected items
  *
  */
export const selectNotification = (checked: boolean, selectedNotification: Notification) => {
  return (dispatch: Function, getState: Function) => {
    const {
      notificationsPanel: {
        panel: {selected}
      }
    } = getState();
    const updatedSelected = [...selected];
    const matchingIndex = updatedSelected.findIndex(item =>
      item?.uuid === selectedNotification?.uuid
    );
    if (matchingIndex > -1) {
      if (!checked) updatedSelected.splice(matchingIndex, 1);
    } else {
      updatedSelected.push(selectedNotification);
    }
    dispatch({type: SELECT_NOTIFICATION, selectedNotifications: updatedSelected});
  };
};

/**
  *
  * Selects all notifications for the active tab
  * multi-select
  */
export const selectAllNotifications = () => {
  return (dispatch: Function, getState: Function) => {
    const {
      notificationsPanel: {
        panel: {activePanel},
        all,
        urgent
      }
    } = getState();
    dispatch({
      type          : SELECT_ALL_NOTIFICATIONS,
      notifications : {
        all    : activePanel ? all : [],
        urgent : !activePanel ? urgent : []
      }
    });
  };
};

/**
  *
  * Deselects all notifications for the active tab
  * multi-select
  */
export const deselectAllNotifications = () => {
  return (dispatch: Function, getState: Function) => {
    const {
      notificationsPanel: {
        panel: {activePanel},
        all,
        urgent
      }
    } = getState();
    dispatch({
      type          : DESELECT_ALL_NOTIFICATIONS,
      notifications : {
        all    : activePanel ? all : [],
        urgent : !activePanel ? urgent : []
      }
    });
  };
};

/**
 *
 * Invoked when the filter option is selected/closed from the Notification panel.
 */
export const showNotificationFilters = (show = true) => ({
  type : SHOW_NOTIFICATION_FILTERS, show
});

/**
 * Announces one job notification the owning application has just created, so
 * the badge and the panel carry it without waiting on a reload.
 *
 * `POST_INITIALIZATION_COMPLETE` hydrates this reducer from that application's
 * own store, and it fires once - at sign-in. Anything created afterwards
 * reaches the reducer only through here.
 *
 * ```typescript
 *
 *   import {jobNotificationCreated} from '@ion/applications/src/notifications/state';
 *
 *   dispatch(jobNotificationCreated(toJobNotification(entry)));
 *
 * ```
 *
 * @param   notification The created notification, already in panel shape.
 * @returns The action to dispatch.
 */
export const jobNotificationCreated = (notification: Notification) => ({
  type : JOB_NOTIFICATION_CREATED, notification
});

/**
 * When invoked, the automated notifications refresh will be deactivated.
 */
export const deactivateRefresh = () => {
  return async() => {
    NotificationsWorker.destruct();
  };
};

// invoked when the global group has been changed/updated.
export const resetNotifications = () => {
  return async(dispatch, getState) => {
    const {globalGroup} = getState();
    try {
      dispatch({type: RESET_NOTIFICATION_REQUEST});
      NotificationsWorker.onAccountChange(toNotificationsAccount(globalGroup));
    } catch (err) {
      error(err);
      dispatch({type: RESET_NOTIFICATION_ERROR});
    }
  };
};

/**
 * REDUCERS
 */
export interface NotificationsPanelState extends AsyncState {
  all: Notification[] | null;
  urgent: Notification[] | null;
  latest: Notification[] | null;
  unreadCount: number;
  refreshing: boolean;
  updating: boolean;
  loadingDetails: boolean;
  isFilter: boolean;
  selectedFilter: GenericObject;
  panel: {
    activePanel: NotificationPanelTabs;
    open: boolean;
    state: PanelState[];
    selected: Notification[]
  };
}

export interface PanelState {
  id: string;
  open: boolean;
  loading: boolean;
  isFilter: boolean;
}

// defines the initial state for the reducer ...
export const initialState: NotificationsPanelState = {
  all            : [],
  urgent         : [],
  latest         : [],
  unreadCount    : 0,
  loading        : false,
  selectedFilter : {},
  error          : null,
  refreshing     : false,
  updating       : false,
  loadingDetails : false,
  isFilter       : false,
  panel          : {
    activePanel : NotificationPanelTabs.urgent,
    open        : false,
    state       : [],
    selected    : []
  }
};

// defines the notifications reducers ...
const reducers = {
  [POST_INITIALIZATION_REQUEST] : (draft: NotificationsPanelState) => {
    draft.loading = true;
  },
  [POST_INITIALIZATION_COMPLETE] : (draft: NotificationsPanelState, {notifications}) => {
    setNotifications(draft, notifications);
  },
  [POST_INITIALIZATION_ERROR] : (draft: NotificationsPanelState) => {
    draft.loading = false;
  },
  [SET_NOTIFICATIONS_PANEL_OPEN] : ({panel}: NotificationsPanelState, {open}) => {
    panel.open = open;
  },
  [SET_NOTIFICATIONS_ACTIVE_PANEL] : ({panel}: NotificationsPanelState, {activePanel}) => {
    panel.activePanel = activePanel;
  },
  [REMOVE_TOAST_NOTIFICATION] : (draft: NotificationsPanelState, {index}) => {
    draft.latest.splice(index, 1);
  },
  [GET_NOTIFICATION_DETAILS_REQUEST] : (draft: NotificationsPanelState) => {
    draft.loadingDetails = true;
  },
  [GET_NOTIFICATION_DETAILS_RESPONSE] : (draft: NotificationsPanelState, {notification}
  ) => {
    updateNotification(draft.all, notification);
    if (notification.isUrgent) {
      updateNotification(draft.urgent, notification);
    }
    draft.loadingDetails = false;
    const {uuid} = notification;
    const existingState = [...draft.panel.state];
    const matchingIndex = existingState.findIndex(item => item.id === uuid);
    if (matchingIndex > -1) {
      existingState[matchingIndex] = {id: uuid, open: true, loading: false};
      const updatedState = existingState;
      draft.panel.state = updatedState;
    } else {
      draft.panel.state = [...existingState, {id: uuid, open: true, loading: false}];
    }
  },
  [GET_NOTIFICATION_DETAILS_ERROR] : ({loadingDetails}: NotificationsPanelState) => {
    loadingDetails = true;
  },
  [SHOW_NOTIFICATION_FILTERS] : (draft: NotificationsPanelState, {show}) => {
    draft.isFilter = show;
  },
  [MARK_AS_READ_REQUEST] : (draft: NotificationsPanelState) => {
    draft.updating = true;
  },
  [MARK_AS_READ_RESPONSE] : (draft: NotificationsPanelState, {notifications}) => {
    draft.unreadCount = getUnreadCount(draft, notifications);
    draft.all = updateNotifications(draft.all, notifications, notification => {
      return {...notification, isAcknowledged: true, isRead: true};
    });
    draft.urgent = updateNotifications(draft.urgent, notifications, notification => {
      return {...notification, isAcknowledged: true, isRead: true};
    });
    draft.updating = false;
  },
  [MARK_AS_READ_ERROR] : (draft: NotificationsPanelState) => {
    draft.updating = false;
  },
  [MARK_AS_CLEARED_REQUEST] : ({updating}: NotificationsPanelState) => {
    updating = true;
  },
  [MARK_AS_CLEARED_RESPONSE] : (draft: NotificationsPanelState, {notifications}) => {
    const ids = idMap(notifications);
    const filter = ({id}) => !ids[id];
    draft.all = draft.all?.filter(filter);
    draft.urgent = draft.urgent?.filter(filter);
    draft.unreadCount = getUnreadCount(draft, notifications);
    draft.updating = false;
  },
  [MARK_AS_CLEARED_ERROR] : ({updating}: NotificationsPanelState) => {
    updating = false;
  },
  [ALERTS_REFRESH_UPDATED] : (draft: NotificationsPanelState, {latest}) => {
    const {all, urgent, unreadCount} = normalize(latest);
    draft.all = all.concat(draft.all);
    draft.urgent = urgent.concat(draft.urgent);
    draft.unreadCount += unreadCount;
    draft.latest = latest;
  },
  [JOB_NOTIFICATION_CREATED] : (draft: NotificationsPanelState, {notification}) => {
    const {all, urgent, unreadCount} = normalize([notification]);
    draft.all = all.concat(draft.all ?? []);
    draft.urgent = urgent.concat(draft.urgent ?? []);
    draft.unreadCount += unreadCount;
  },
  [SET_NOTIFICATION_PANEL_STATE] : ({panel}: NotificationsPanelState, state) => {
    panel.state = [...state.newState];
  },
  [SELECT_NOTIFICATION] : (draft: NotificationsPanelState, {selectedNotifications}) => {
    draft.panel.selected = [...selectedNotifications];
  },
  [SELECT_ALL_NOTIFICATIONS] : (draft: NotificationsPanelState, {notifications}) => {
    draft.panel.selected = [...notifications.all, ...notifications.urgent];
  },
  [DESELECT_ALL_NOTIFICATIONS] : (draft: NotificationsPanelState, {notifications}) => {
    draft.panel.selected = [];
  },
  [RESET_NOTIFICATION_REQUEST] : (draft: NotificationsPanelState, {params}) => {
    draft.loading = true;
    draft.selectedFilter = params;
  },
  [RESET_NOTIFICATION_RESPONSE] : (draft: NotificationsPanelState, {notifications}) => {
    draft.isFilter = false;
    setNotifications(draft, notifications);
  },
  [RESET_NOTIFICATION_ERROR] : (draft: NotificationsPanelState) => {
    draft.isFilter = false;
    draft.loading = false;
  }
};

/**
 * Provides a utility which, given a list of AlertFired entities, converts them
 * to a collection of Notification types suitable for use within iOn Suite.
 */
export const normalize = (
  alerts: AlertFired[]
): { all: Notification[], urgent: Notification[], unreadCount: number } => {
  const all = [];
  const urgent = [];
  let unreadCount = 0;
  alerts.forEach((alert: AlertFired) => {
    const category = categoryMap[alert.severity];
    const isUrgent = category === NotificationCategory.Urgent;
    const isRead = alert.isAcknowledged === true;
    const notification = {
      action    : isRead ? NotificationAction.Read : null,
      isCleared : false,
      isUrgent,
      isRead,
      category,
      ...alert
    };
    !isRead && unreadCount++;
    isUrgent && urgent.push(notification);
    all.push(notification);
  });
  return {all, urgent, unreadCount};
};

export const categoryMap = {
  [AlertSeverity.CRITICAL] : NotificationCategory.Urgent,
  [AlertSeverity.HIGH]     : NotificationCategory.General,
  [AlertSeverity.MEDIUM]   : NotificationCategory.TimeRelated,
  [AlertSeverity.LOW]      : NotificationCategory.ConnectivitySignal,
  [AlertSeverity.SYSTEM]   : NotificationCategory.ScheduledEvents
};

export const getUnreadCount = ({all, unreadCount}: NotificationsPanelState,
  notifications: Notification[]): number => {
  let unread = 0;
  notifications.forEach(({id, isRead}) => {
    if (!isUndefined(isRead) && !isRead) {
      unread++;
    } else {
      unread += all.filter(item => item.id === id && !item.isRead)?.length;
    }
  });
  return unreadCount - unread;
};

// provides a utility which updates a notification within a collection of notifications ...
const updateNotification = (items: Notification[], notification: Notification) => {
  const index = items.findIndex(({id}) => notification.id === id);
  if (index !== -1) {
    items[index] = notification;
  }
};

// provides a utility which updates a list of notifications notification within a collection
// of notifications ...
export const updateNotifications = (
  items: Notification[], updated: Notification[], updater: (item: Notification) => Notification
): Notification[] => {
  const ids = idMap(updated);
  return items.map(notification => {
    const item = ids[notification.id];
    if (item) {
      return updater(item);
    }
    return notification;
  });
};

// provides a utility that normalizes the notification API response into the required format
// for the application to set the different states.
export const setNotifications = (draft: NotificationsPanelState, notifications: Notification[]) => {
  const {all, urgent, unreadCount} = normalize(notifications);
  draft.all                        = all;
  draft.urgent                     = urgent;
  draft.unreadCount                = unreadCount;
  draft.loading                    = false;
};

// defines all reducers for actions of interest to the this reducer ...
export default produce((draft: NotificationsPanelState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});
